import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchListings, createListing, updateListing, deleteListing } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import ImageUploader from '@/components/ImageUploader';

const ManageListings = () => {
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    description_fr: '',
    description_en: '',
    type: '',
    location: '',
    rent: '',
    deposit: '',
    duration: '',
    available_from: '',
    features: '',
    images: [],
    published: true
  });

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    try {
      const data = await fetchListings();
      setListings(data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingListing(null);
    setFormData({
      title_fr: '',
      title_en: '',
      description_fr: '',
      description_en: '',
      type: '',
      location: '',
      rent: '',
      deposit: '',
      duration: '',
      available_from: '',
      features: '',
      images: [],
      published: true
    });
    setDialogOpen(true);
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setFormData({
      title_fr: listing.title_fr,
      title_en: listing.title_en,
      description_fr: listing.description_fr,
      description_en: listing.description_en,
      type: listing.type,
      location: listing.location,
      rent: listing.rent.toString(),
      deposit: listing.deposit.toString(),
      duration: listing.duration,
      available_from: listing.available_from,
      features: listing.features.join(', '),
      images: listing.images || [],
      published: listing.published !== undefined ? listing.published : true
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.images.length === 0) {
      toast.error('Veuillez téléverser au moins une image');
      return;
    }
    
    const payload = {
      ...formData,
      rent: parseFloat(formData.rent),
      deposit: parseFloat(formData.deposit),
      features: formData.features.split(',').map(f => f.trim()).filter(f => f)
    };

    try {
      if (editingListing) {
        await updateListing(editingListing.listing_id, payload);
        toast.success('Annonce modifiée avec succès');
      } else {
        await createListing(payload);
        toast.success('Annonce créée avec succès');
      }
      setDialogOpen(false);
      loadListings();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteListing(id);
      toast.success('Annonce supprimée');
      loadListings();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublished = async (listing) => {
    try {
      await updateListing(listing.listing_id, { published: !listing.published });
      toast.success(listing.published ? 'Annonce mise en brouillon' : 'Annonce publiée');
      loadListings();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="manage-listings">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Gérer les logements</h1>
            <p className="text-slate-600 mt-2">{listings.length} annonce(s) au total</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-listing-btn" 
                onClick={handleAdd} 
                className="bg-[#127abb] hover:bg-[#0e6292] rounded-full shadow-lg"
              >
                <Plus size={20} className="mr-2" />
                Nouvelle annonce
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {editingListing ? '✏️ Modifier l\'annonce' : '➕ Nouvelle annonce'}
                </DialogTitle>
                <DialogDescription>
                  {editingListing ? 'Modifiez les informations du logement' : 'Créez une nouvelle annonce de logement'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {/* Images Upload */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <ImageUploader
                    images={formData.images}
                    onChange={(images) => setFormData({...formData, images})}
                    category="listings"
                  />
                </div>

                {/* Titles */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Titre (Français) *</label>
                    <Input 
                      value={formData.title_fr} 
                      onChange={(e) => setFormData({...formData, title_fr: e.target.value})} 
                      required 
                      placeholder="Studio lumineux Lyon 5e"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Title (English) *</label>
                    <Input 
                      value={formData.title_en} 
                      onChange={(e) => setFormData({...formData, title_en: e.target.value})} 
                      required 
                      placeholder="Bright studio Lyon 5th"
                    />
                  </div>
                </div>

                {/* Descriptions */}
                <div>
                  <label className="block text-sm font-medium mb-2">Description (Français) *</label>
                  <Textarea 
                    value={formData.description_fr} 
                    onChange={(e) => setFormData({...formData, description_fr: e.target.value})} 
                    required 
                    rows={3}
                    placeholder="Décrivez le logement en détail..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description (English) *</label>
                  <Textarea 
                    value={formData.description_en} 
                    onChange={(e) => setFormData({...formData, description_en: e.target.value})} 
                    required 
                    rows={3}
                    placeholder="Describe the property in detail..."
                  />
                </div>

                {/* Type and Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Type *</label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Studio">Studio</SelectItem>
                        <SelectItem value="T1">T1</SelectItem>
                        <SelectItem value="T2">T2</SelectItem>
                        <SelectItem value="T3">T3</SelectItem>
                        <SelectItem value="Colocation">Colocation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Localisation *</label>
                    <Input 
                      value={formData.location} 
                      onChange={(e) => setFormData({...formData, location: e.target.value})} 
                      required 
                      placeholder="Lyon 5e - Vieux Lyon"
                    />
                  </div>
                </div>

                {/* Rent, Deposit, Duration */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Loyer (€) *</label>
                    <Input 
                      type="number" 
                      value={formData.rent} 
                      onChange={(e) => setFormData({...formData, rent: e.target.value})} 
                      required 
                      min="0"
                      step="10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Caution (€) *</label>
                    <Input 
                      type="number" 
                      value={formData.deposit} 
                      onChange={(e) => setFormData({...formData, deposit: e.target.value})} 
                      required 
                      min="0"
                      step="10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Durée *</label>
                    <Input 
                      value={formData.duration} 
                      onChange={(e) => setFormData({...formData, duration: e.target.value})} 
                      required 
                      placeholder="9 mois minimum"
                    />
                  </div>
                </div>

                {/* Available from */}
                <div>
                  <label className="block text-sm font-medium mb-2">Disponible à partir de *</label>
                  <Input 
                    value={formData.available_from} 
                    onChange={(e) => setFormData({...formData, available_from: e.target.value})} 
                    required 
                    placeholder="01/02/2026"
                  />
                </div>

                {/* Features */}
                <div>
                  <label className="block text-sm font-medium mb-2">Caractéristiques (séparées par des virgules)</label>
                  <Input 
                    value={formData.features} 
                    onChange={(e) => setFormData({...formData, features: e.target.value})} 
                    placeholder="Meublé, WiFi, Proche métro, Balcon"
                  />
                </div>

                {/* Published Status */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="w-4 h-4 text-[#127abb] rounded focus:ring-[#127abb]"
                  />
                  <label htmlFor="published" className="text-sm font-medium text-slate-700">
                    Publier l'annonce immédiatement
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#127abb] hover:bg-[#0e6292]"
                  >
                    {editingListing ? 'Mettre à jour' : 'Créer l\'annonce'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="bg-white border border-stone-100 shadow-lg rounded-2xl p-12 text-center">
            <p className="text-slate-600 text-lg mb-4">Aucune annonce pour le moment</p>
            <Button onClick={handleAdd} className="bg-[#127abb] hover:bg-[#0e6292]">
              <Plus size={20} className="mr-2" />
              Créer la première annonce
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-stone-100 shadow-lg rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Image</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Titre</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Type</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Localisation</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Loyer</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Statut</th>
                  <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => (
                  <tr key={listing.listing_id} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      {listing.images && listing.images.length > 0 ? (
                        <img 
                          src={listing.images[0]} 
                          alt="Preview" 
                          className="w-16 h-16 object-cover rounded-lg shadow"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                          <span className="text-slate-400 text-xs">Pas d'image</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="font-medium">{listing.title_fr}</div>
                      {listing.images && (
                        <div className="text-xs text-slate-500 mt-1">
                          {listing.images.length} image(s)
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-primary-50 text-[#127abb] rounded-full text-xs font-medium">
                        {listing.type}
                      </span>
                    </td>
                    <td className="p-4">{listing.location}</td>
                    <td className="p-4 font-semibold">{listing.rent}€</td>
                    <td className="p-4">
                      <button
                        onClick={() => togglePublished(listing)}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          listing.published 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                        }`}
                      >
                        {listing.published ? (
                          <><Eye size={12} /> Publié</>
                        ) : (
                          <><EyeOff size={12} /> Brouillon</>
                        )}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleEdit(listing)} 
                          data-testid={`edit-listing-${listing.listing_id}`}
                          className="hover:bg-primary-50 hover:text-[#127abb] hover:border-[#127abb]"
                        >
                          <Pencil size={16} />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-[#8c1616] border-[#8c1616] hover:bg-red-50" 
                              data-testid={`delete-listing-${listing.listing_id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                              <AlertDialogDescription>
                                Êtes-vous sûr de vouloir supprimer cette annonce ? Cette action est irréversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(listing.listing_id)} 
                                className="bg-[#8c1616] hover:bg-[#701111]"
                              >
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManageListings;